/**
 * ContactDuplicatesCard v2 — Admin Diagnostics (2026-02).
 *
 * READ-ONLY. Hits `GET /api/admin/diagnostics/contact-duplicates` and
 * renders: summary tiles + a SAFE `name+phone` group table (with KEEP /
 * remove-candidate labels — suggestion only, no action buttons) + a
 * clearly separated informational `email` group table. No Apply, no
 * Fix, no Delete controls.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Users2, Copy, RefreshCw, AlertTriangle, CheckCircle2, XCircle,
  Info, AlertOctagon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function StatBox({ label, value, tone = 'slate', testId }) {
  const map = {
    slate: 'bg-slate-100 text-slate-900 border-slate-300',
    amber: 'bg-amber-100 text-amber-900 border-amber-300',
    rose: 'bg-rose-100 text-rose-900 border-rose-300',
    emerald: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    indigo: 'bg-indigo-100 text-indigo-900 border-indigo-300',
  };
  return (
    <div className={`p-3 rounded-lg border ${map[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">{label}</div>
      <div className="text-2xl font-mono font-semibold">{value}</div>
    </div>
  );
}

function Yes({ v }) {
  return v ? (
    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 inline" />
  ) : (
    <XCircle className="w-3.5 h-3.5 text-slate-300 inline" />
  );
}

function NamePhoneGroupTable({ groups, testId }) {
  if (!groups || groups.length === 0) {
    return (
      <div className="text-xs italic px-2 py-2 border rounded bg-emerald-50 border-emerald-200 text-emerald-800" data-testid={`${testId}-empty`}>
        no safe duplicate groups detected
      </div>
    );
  }
  return (
    <div className="border rounded max-h-[540px] overflow-auto" data-testid={testId}>
      {groups.map((g, gi) => (
        <details
          key={`${g.normalized_name}-${g.normalized_phone}-${gi}`}
          className={`border-b last:border-b-0 ${g.same_id_bug_detected ? 'bg-rose-50' : ''}`}
          data-testid={`${testId}-group-${gi}`}
          open={gi < 3}
        >
          <summary className="p-2 cursor-pointer flex items-center gap-2 flex-wrap">
            <Badge className="bg-slate-800 text-white font-mono text-[11px]">×{g.copies}</Badge>
            <span className="font-medium text-slate-900" data-testid={`${testId}-group-${gi}-name`}>{g.display_name}</span>
            <span className="text-[11px] font-mono text-slate-500">phone={g.normalized_phone}</span>
            <span className="text-[11px] text-slate-600 ml-2">
              KEEP 1 · remove candidates {g.remove_candidates}
            </span>
            {g.same_id_bug_detected && (
              <Badge className="bg-rose-100 text-rose-900 border-rose-300 ml-auto">SAME-ID BUG</Badge>
            )}
          </summary>
          <table className="w-full text-[11px] border-t bg-white">
            <thead className="bg-slate-100">
              <tr className="text-left">
                <th className="p-1.5">role</th>
                <th className="p-1.5">display name</th>
                <th className="p-1.5">raw phone</th>
                <th className="p-1.5">raw email</th>
                <th className="p-1.5">created_at</th>
                <th className="p-1.5 text-center">phone</th>
                <th className="p-1.5 text-center">email</th>
                <th className="p-1.5 text-center">addr</th>
                <th className="p-1.5 text-center">project</th>
                <th className="p-1.5 text-center">notes</th>
                <th className="p-1.5 text-center">score</th>
                <th className="p-1.5 text-center">source</th>
                <th className="p-1.5">record id</th>
              </tr>
            </thead>
            <tbody>
              {g.members.map((m, mi) => (
                <tr key={`${m.raw_id}-${mi}`}
                    className={`border-t ${m.keep ? 'bg-emerald-50' : ''}`}
                    data-testid={`${testId}-group-${gi}-member-${mi}`}>
                  <td className="p-1.5">
                    {m.keep ? (
                      <Badge className="bg-emerald-600 text-white text-[10px]" data-testid={`${testId}-group-${gi}-member-${mi}-role`}>KEEP</Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-700 border-slate-300 text-[10px]" data-testid={`${testId}-group-${gi}-member-${mi}-role`}>remove candidate</Badge>
                    )}
                  </td>
                  <td className="p-1.5">{m.display_name || <em className="text-slate-400">(unnamed)</em>}</td>
                  <td className="p-1.5 font-mono">
                    {m.raw_phone || <em className="text-slate-400">—</em>}
                    {m.phone_malformed && <span className="ml-1 text-[10px] text-amber-700">malformed</span>}
                  </td>
                  <td className="p-1.5 font-mono">{m.raw_email || <em className="text-slate-400">—</em>}</td>
                  <td className="p-1.5 font-mono">{m.created_at || <em className="text-slate-400">—</em>}</td>
                  <td className="p-1.5 text-center"><Yes v={m.populated.phone} /></td>
                  <td className="p-1.5 text-center"><Yes v={m.populated.email} /></td>
                  <td className="p-1.5 text-center"><Yes v={m.populated.address} /></td>
                  <td className="p-1.5 text-center"><Yes v={m.populated.assigned_to_project} /></td>
                  <td className="p-1.5 text-center"><Yes v={m.populated.notes} /></td>
                  <td className="p-1.5 text-center font-mono font-semibold">{m.populated_score}</td>
                  <td className="p-1.5 text-center">
                    {m.recovered_by_migration_0060
                      ? <Badge className="bg-indigo-100 text-indigo-900 border-indigo-300 text-[10px]">0060</Badge>
                      : <span className="text-slate-400 text-[10px]">pre-existing</span>}
                  </td>
                  <td className="p-1.5 font-mono break-all">{m.raw_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ))}
    </div>
  );
}

function EmailGroupTable({ groups, testId }) {
  if (!groups || groups.length === 0) {
    return (
      <div className="text-xs italic px-2 py-2 border rounded bg-emerald-50 border-emerald-200 text-emerald-800" data-testid={`${testId}-empty`}>
        no email duplicate groups detected
      </div>
    );
  }
  return (
    <div className="border rounded max-h-[420px] overflow-auto" data-testid={testId}>
      {groups.map((g, gi) => (
        <details
          key={`${g.email}-${gi}`}
          className={`border-b last:border-b-0 ${g.same_id_bug_detected ? 'bg-rose-50' : ''}`}
          data-testid={`${testId}-group-${gi}`}
        >
          <summary className="p-2 cursor-pointer flex items-center gap-2 flex-wrap">
            <Badge className="bg-slate-500 text-white font-mono text-[11px]">×{g.copies}</Badge>
            <span className="font-medium text-slate-900">{g.email}</span>
            <Badge className={g.different_display_names ? 'bg-rose-100 text-rose-900 border-rose-300' : 'bg-slate-100 text-slate-800 border-slate-300'}>
              {g.warning}
            </Badge>
          </summary>
          <table className="w-full text-[11px] border-t bg-white">
            <thead className="bg-slate-100">
              <tr className="text-left">
                <th className="p-1.5">display name</th>
                <th className="p-1.5">raw phone</th>
                <th className="p-1.5">raw email</th>
                <th className="p-1.5">created_at</th>
                <th className="p-1.5 text-center">source</th>
                <th className="p-1.5">record id</th>
              </tr>
            </thead>
            <tbody>
              {g.members.map((m, mi) => (
                <tr key={`${m.raw_id}-${mi}`} className="border-t" data-testid={`${testId}-group-${gi}-member-${mi}`}>
                  <td className="p-1.5">{m.display_name || <em className="text-slate-400">(unnamed)</em>}</td>
                  <td className="p-1.5 font-mono">{m.raw_phone || <em className="text-slate-400">—</em>}</td>
                  <td className="p-1.5 font-mono">{m.raw_email || <em className="text-slate-400">—</em>}</td>
                  <td className="p-1.5 font-mono">{m.created_at || <em className="text-slate-400">—</em>}</td>
                  <td className="p-1.5 text-center">
                    {m.recovered_by_migration_0060
                      ? <Badge className="bg-indigo-100 text-indigo-900 border-indigo-300 text-[10px]">0060</Badge>
                      : <span className="text-slate-400 text-[10px]">pre-existing</span>}
                  </td>
                  <td className="p-1.5 font-mono break-all">{m.raw_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ))}
    </div>
  );
}

export default function ContactDuplicatesCard({ isSuperAdmin }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/diagnostics/contact-duplicates`,
        { headers: authHeaders() },
      );
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Request failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const copyJSON = () => {
    if (!data) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success('Copied duplicates report as JSON');
    } catch { /* ignore */ }
  };

  if (!isSuperAdmin) return null;
  const s = data?.summary;

  return (
    <div
      className="p-5 rounded-xl border border-slate-300 bg-white mb-6"
      data-testid="contact-duplicates-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <Users2 className="w-5 h-5 text-slate-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold">Contact Duplicates</h3>
          <p className="text-sm text-slate-700">
            Read-only report. Scans <code>contacts</code> for this company. The
            <strong> Safe name + phone </strong> section requires BOTH a non-empty
            display name AND a well-formed phone (≥10 digits, leading US &quot;1&quot;
            stripped) — this is the ONLY section any future cleanup should act on. The
            <strong> Email </strong> section is informational only. No delete or merge
            controls exist anywhere on this card.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={run} disabled={busy} data-testid="contact-duplicates-run-btn">
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning…</>
            ) : data ? (
              <><RefreshCw className="w-4 h-4 mr-2" /> Re-run</>
            ) : (
              <><Users2 className="w-4 h-4 mr-2" /> Run Contact Duplicates</>
            )}
          </Button>
          {data && (
            <Button variant="ghost" size="sm" onClick={copyJSON} data-testid="contact-duplicates-copy-btn">
              <Copy className="w-4 h-4 mr-1" /> Copy JSON
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-2" data-testid="contact-duplicates-error">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <div className="font-semibold">Report failed</div>
            <div className="text-xs">{error}</div>
          </div>
        </div>
      )}

      {data && (
        <div className="space-y-5">
          <div
            className={`p-4 rounded-lg border ${
              s.same_id_bug_detected
                ? 'bg-rose-50 border-rose-300'
                : s.safe_name_phone_groups === 0
                  ? 'bg-emerald-50 border-emerald-300'
                  : 'bg-amber-50 border-amber-300'
            }`}
            data-testid="contact-duplicates-summary"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-sm">Summary</span>
              <Badge className={s.same_id_bug_detected ? 'bg-rose-100 text-rose-900 border-rose-300' : 'bg-emerald-100 text-emerald-900 border-emerald-300'}>
                {s.same_id_bug_sentence}
              </Badge>
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatBox label="Total contacts scanned" value={s.total_contacts_scanned} tone="slate" testId="dup-stat-scanned" />
              <StatBox label="Safe name+phone groups" value={s.safe_name_phone_groups} tone={s.safe_name_phone_groups ? 'amber' : 'emerald'} testId="dup-stat-safe-groups" />
              <StatBox label="Total remove candidates" value={s.total_remove_candidates} tone={s.total_remove_candidates ? 'rose' : 'emerald'} testId="dup-stat-remove-candidates" />
              <StatBox label="Informational email groups" value={s.informational_email_groups} tone="slate" testId="dup-stat-email-groups" />
              <StatBox label="Remove cand: from 0060" value={s.remove_candidates_recovered_by_0060} tone="indigo" testId="dup-stat-remove-from-0060" />
              <StatBox label="Remove cand: pre-existing" value={s.remove_candidates_pre_existing} tone="slate" testId="dup-stat-remove-pre-existing" />
              <StatBox label="N+P members: from 0060" value={s.name_phone_group_members_recovered_by_0060} tone="indigo" testId="dup-stat-np-from-0060" />
              <StatBox label="N+P members: pre-existing" value={s.name_phone_group_members_pre_existing} tone="slate" testId="dup-stat-np-pre-existing" />
            </div>
          </div>

          <section data-testid="contact-duplicates-section-name-phone">
            <h4 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2">
              Safe duplicate groups — <em>name + phone</em>
              <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 text-[10px]">the only section future cleanup should act on</Badge>
            </h4>
            <NamePhoneGroupTable groups={data.groups_by_name_phone} testId="contact-duplicates-table-np" />
          </section>

          <section data-testid="contact-duplicates-section-email">
            <div className="p-3 mb-2 rounded border bg-slate-50 border-slate-200 flex items-start gap-2 text-xs text-slate-700">
              <Info className="w-4 h-4 mt-0.5 text-slate-500" />
              <div>
                <div className="font-semibold text-slate-900">Informational only — NOT safe to auto-merge (people can share an email).</div>
                <div>
                  Any group whose members have different display names is flagged with a
                  <span className="mx-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-900 border border-rose-300">different people — do not merge</span>
                  badge. Use this section for investigation only.
                </div>
              </div>
            </div>
            <h4 className="text-sm font-semibold text-slate-800 mb-1">
              Duplicate groups by normalized <em>email</em>
              <AlertOctagon className="w-3.5 h-3.5 inline ml-1 text-rose-500" />
            </h4>
            <EmailGroupTable groups={data.groups_by_email} testId="contact-duplicates-table-email" />
          </section>

          <section data-testid="contact-duplicates-notes">
            <details>
              <summary className="text-xs text-slate-500 cursor-pointer">Grouping rules &amp; guarantees</summary>
              <ul className="mt-1 text-xs text-slate-600 list-disc pl-5 space-y-1">
                {Object.entries(data.notes || {}).map(([k, v]) => (
                  <li key={k}><strong>{k}</strong>: {v}</li>
                ))}
              </ul>
            </details>
          </section>
        </div>
      )}
    </div>
  );
}
